import { useEffect, useState, useContext, useCallback, useRef } from 'react';
import Pagination from '@/components/Common/Pagination.tsx';
import { Select } from '@/components/Controls/Select';
import { PageRequest, Processing } from '@/api/entities/processing.ts';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import {
  useGetNextDataReport,
  useGetAllProcessing,
  useGetAllComponentKey,
  useGetAllDefectKey,
  useDeleteFirestoreImageByImageUrl,
  useDeleteProcessing,
} from '@/hooks/ai.hooks';
import { mapComponentOptions } from '@/utils/ia.utils';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import Card from '@/components/Card';
import { TableTitle } from '@/components/Header/TableTitle';
import { TableHead } from '@/components/Datatable/TableHead';
import { StatusTag } from '@/components/Tag/StatusTag';
import Loader from '@/components/Loading/Loader';
import { TableCell } from '@/components/Datatable/TableCell';
import { HumanModerationStatus } from '@/api/entities/moderation';
import { CellContentWithRedirect } from '@/components/Custom/Datatable/CellContentWithRedirect';
import { NoTableDataFound } from '@/components/Custom/NotFound/NoTableDataFound';
import { useLocalStorageState } from '@/hooks/local-storage.hooks';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { EngineCorrectionUploadModal } from '@/components/Custom/Modal/EngineCorrectionUploadModal';
import { showModal } from '@/utils/modal.utils';
import ButtonAdvancedFilter from '@/components/Custom/Button/ButtonAdvancedFilter';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import { ImagePreviewModal } from '@/components/Custom/Modal/ImagePreviewModal';

const initialPageRequest: PageRequest = {
  page: 1,
  limit: 100,
  status: 'all',
  isManualUpload: 'all',
  key: '',
  defectKey: '',
};

export const EngineCorrectionListPage = () => {
  const { t } = useTranslation('engineCorrection');
  const { currentPage, setCurrentPage } = useContext(EngineCorrectionContext);
  initialPageRequest.page = currentPage;
  const ID_INIT = '64caef2d9f0e53ad5c5f2c13';
  const [filter, setFilter] = useState<PageRequest>(initialPageRequest);
  const [status, setStatus] = useLocalStorageState({
    key: 'engineCorrection_status',
    defaultValue: 'all',
  });
  const [isManualUpload, setIsManualUpload] = useLocalStorageState({
    key: 'engineCorrection_uploadedImagesStatus',
    defaultValue: 'all',
  });
  const [componentKey, setComponentKey] = useLocalStorageState({
    key: 'engineCorrection_componentKey',
  });
  const [defectKey, setDefectKey] = useLocalStorageState({
    key: 'engineCorrection_defectKey',
  });
  const filtersEmptyRef = useRef<boolean>(true);
  const { data, isLoading, isFetching } = useGetAllProcessing(filter);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [processings, setProcessings] = useState<Processing[]>([]);
  const [selectedProcessing, setSelectedProcessing] = useState<Processing>();
  const [isUploadingImages, setIsUploadingImages] = useState<boolean>(false);

  const { data: dataToModerate } = useGetNextDataReport(ID_INIT);
  const { data: keyCars, isLoading: isLoadingKeyCars } =
    useGetAllComponentKey();
  const { data: defectKeyCars, isLoading: isLoadingDefectKeyCars } =
    useGetAllDefectKey();

  const { mutate: deleteProcessing, isPending: isPendingDeleteProcessing } =
    useDeleteProcessing({
      t,
      handleSuccess: () => {
        deleteFirestoreImage(selectedProcessing?.imageUrl);
      },
    });

  const {
    mutate: deleteFirestoreImage,
    isPending: isPendingDeleteFirestoreImage,
  } = useDeleteFirestoreImageByImageUrl({
    t,
    handleSuccess: () => {
      setSelectedProcessing(undefined);
    },
  });

  useEffect(() => {
    // Clear the current data immediately when filters change to prevent showing stale data
    setProcessings([]);
    setTotalPages(0);

    // Reset page to 1 when filters change
    setCurrentPage(1);

    setFilter((prevFilter) => ({
      ...prevFilter,
      page: 1, // Always reset to page 1 when filters change
      status: status,
      isManualUpload: isManualUpload,
      key: componentKey,
      defectKey: defectKey,
    }));

    filtersEmptyRef.current =
      status === 'all' &&
      isManualUpload === 'all' &&
      !componentKey &&
      !defectKey;
  }, [status, isManualUpload, componentKey, defectKey, setCurrentPage]);

  const isLoadingAll =
    isLoading ||
    isFetching ||
    isLoadingKeyCars ||
    isLoadingDefectKeyCars ||
    isUploadingImages ||
    isPendingDeleteProcessing ||
    isPendingDeleteFirestoreImage;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilter({
      ...filter,
      page,
    });
  };

  useEffect(() => {
    setProcessings(data?.data?.data ?? []);
    setTotalPages(data?.data?.totalPages ?? 0);
  }, [data]);

  const filterByStatus = (statusValue: string) => {
    setStatus(statusValue);
  };

  const filterByUploadedImagesStatus = (uploadedImagesStatusValue: string) => {
    setIsManualUpload(uploadedImagesStatusValue);
  };

  const filterByLimit = (limit: number) => {
    setFilter({
      ...filter,
      limit,
    });
  };

  const filterByComponentKey = (componentKeyValue: string) => {
    setComponentKey(componentKeyValue);
  };

  const filterByDefectKey = (defectKeyValue: string) => {
    setDefectKey(defectKeyValue);
  };

  const clearFilters = () => {
    setStatus('all');
    setIsManualUpload('all');
    setComponentKey('');
    setDefectKey('');
    setCurrentPage(1);
  };

  const onClickToModerate = useCallback(() => {
    window.location.href = `/engine-correction/${dataToModerate?.data.id}`;
  }, [dataToModerate]);

  const onUploadingImages = (state: boolean) => {
    setIsUploadingImages(state);
  };

  const handleDeleteImage = () => {
    deleteProcessing(selectedProcessing?.id);
  };

  return (
    <>
      <PageHeader
        title={t('engineCorrectionElements')}
        totalItems={data?.data?.total}
        headingElement={
          <PageInfoHeading text={t('common:detailsRelatedToCurrentSection')} />
        }
        actionListElement={
          <>
            <HeadingButton
              buttonClassName="border-purple-5433FF/45 bg-white"
              textClassName="text-primary"
              text={t('common:uploadImages')}
              leftIcon={
                <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-primary" />
              }
              disabled={isLoadingAll}
              onClick={() => {
                showModal('#engine-correction-upload-modal');
              }}
            />
            <HeadingButton
              buttonClassName="border-purple-5433FF/45 bg-blue-50"
              textClassName="text-primary"
              text={t('startModeration')}
              leftIcon={
                <i className="-mt-[1.5px] ki-outline ki-size !text-[18px] text-primary font-bold" />
              }
              disabled={isLoadingAll || !dataToModerate?.data?.id}
              onClick={onClickToModerate}
            />
          </>
        }
      />
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex flex-1">
            <TableTitle text={t('elementList')} />
          </div>
          <div className="flex flex-wrap justify-end gap-x-5 gap-y-3">
            <div className="flex gap-5">
              <Select
                selectClassName="h-[36px] w-max max-w-[280px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                options={mapComponentOptions(keyCars?.data, '1', t)}
                onChange={(e) => filterByComponentKey(e.target.value)}
                value={componentKey}
              />
              <Select
                selectClassName="h-[36px] w-max max-w-[280px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                options={mapComponentOptions(defectKeyCars?.data, '2', t)}
                onChange={(e) => filterByDefectKey(e.target.value)}
                value={defectKey}
              />
            </div>
            <div>
              <ButtonAdvancedFilter>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-[163.5px] text-gray-600 text-sm self-center">
                      {t('imageOrigin_plural')}
                      <span className="ml-1.5">:</span>
                    </div>
                    <Select
                      selectClassName="h-[36px] w-[187px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                      options={[
                        {
                          label: t('allOrigins'),
                          value: 'all',
                        },
                        {
                          label: t('uploaded_feminine'),
                          value: 'true',
                        },
                        {
                          label: t('automatedReceipt'),
                          value: 'false',
                        },
                      ]}
                      value={isManualUpload}
                      onChange={(e) =>
                        filterByUploadedImagesStatus(e.target.value)
                      }
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-[163.5px] text-gray-600 text-sm self-center">
                      {t('common:moderationStatus')}
                      <span className="ml-1.5">:</span>
                    </div>
                    <Select
                      selectClassName="h-[36px] w-[187px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                      options={[
                        {
                          label: t('common:allStatuses'),
                          value: 'all',
                        },
                        {
                          label: t('common:toModerate'),
                          value: 'A_MODERER',
                        },
                        {
                          label: t('common:validated_feminine'),
                          value: 'MODERE',
                        },
                      ]}
                      value={status}
                      onChange={(e) => filterByStatus(e.target.value)}
                    />
                  </div>
                </div>
              </ButtonAdvancedFilter>
            </div>
            <ClearButton
              disabled={filtersEmptyRef.current}
              onClick={clearFilters}
            />
          </div>
        </div>
        <div data-datatable="true" data-datatable-page-size="5">
          <div className="scrollable-x-auto">
            <table
              className="table table-auto table-border"
              data-datatable-table="true"
              id="grid_table"
            >
              <thead>
                <tr>
                  <TableHead
                    text={t('common:image')}
                    thClassName="text-center min-w-[250px]"
                  />
                  <TableHead
                    text={t('common:nbElementsDetected')}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('common:nbDamagesDetected')}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('common:nbCorrectionsOfElements')}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('common:nbCorrectionsOfDamages')}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('imageOrigin')}
                    thClassName="min-w-[140px]"
                    sortable
                  />
                  <TableHead
                    text={t('common:moderation')}
                    thClassName="w-[130px]"
                    textClassName="text-center"
                    sortable
                  />
                  <TableHead thClassName="sticky-action-column" />
                </tr>
              </thead>
              {!isLoading && !isFetching && processings.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={7}>
                      <NoTableDataFound t={t} />
                    </td>
                  </tr>
                </tbody>
              ) : (
                processings.map((item: Processing) => {
                  const isManualUpload = item?.isManualUpload;

                  const isModerated =
                    item?.status === HumanModerationStatus.MODERE;

                  return (
                    <tbody key={item?.id}>
                      <tr className="border-b not-last:border-gray-E5E7EB dark-mode:border-gray-200">
                        <TableCell
                          content={
                            <img
                              className="h-full w-auto object-contain object-center rounded-md m-auto hover:cursor-pointer"
                              alt=""
                              src={item?.imageUrl}
                              onClick={() => {
                                setSelectedProcessing(item);
                                showModal('#engine-correction-preview-modal');
                              }}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.numElementsDetected || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.numDamagesDetected || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.correctedElements || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.correctedDamages || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          tdClassName="text-center"
                          content={
                            <StatusTag
                              text={`${
                                isManualUpload
                                  ? t('uploaded_feminine')
                                  : t('automatedReceipt')
                              }`}
                              className={
                                isManualUpload
                                  ? 'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4 text-xs dark-mode:bg-green-EAFFF2/10 dark-mode:border-green-C9F4D2/10 dark-mode:text-green-12C653/75'
                                  : 'bg-purple-DF00FF/5 border-purple-DF00FF/20 text-purple-DF00FF/75 text-xs'
                              }
                            />
                          }
                        />
                        <TableCell
                          tdClassName="text-center"
                          content={
                            <StatusTag
                              text={`${
                                isModerated
                                  ? t('common:validated_feminine')
                                  : t('common:toModerate')
                              }`}
                              className={
                                isModerated
                                  ? 'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4 text-xs dark-mode:bg-green-EAFFF2/10 dark-mode:border-green-C9F4D2/10 dark-mode:text-green-12C653/75'
                                  : 'bg-red-FFEEF4 border-red-FFCAD6 text-red-F8275A text-xs dark-mode:bg-red-FFEEF4/10 dark-mode:border-red-FFCAD6/10 dark-mode:text-red-F8275A/80 dark-mode:tracking-wider'
                              }
                            />
                          }
                        />
                        <TableCell
                          tdClassName="sticky-action-column"
                          content={
                            <TableMenu disabled={isLoadingAll}>
                              {isModerated && (
                                <SubMenuItem
                                  text={t('alreadyValidated_feminine')}
                                  itemClassName="hover:!bg-transparent"
                                  leftIconClassName="mt-0.5 ki-information-2 !text-[19px]"
                                  disabled
                                />
                                // <SubMenuItem
                                //   text={t('common:moderate')}
                                //   leftIconClassName="mt-0.5 ki-toggle-on !text-[17.26px]"
                                //   onClick={() =>
                                //     (window.location.href = `/engine-correction/${item.id}`)
                                //   }
                                // />
                              )}
                              {!isModerated && (
                                <SubMenuItem
                                  text={t('common:moderate')}
                                  leftIconClassName="mt-0.5 ki-toggle-on !text-[17.26px]"
                                  disabled={
                                    isModerated ||
                                    isPendingDeleteProcessing ||
                                    isPendingDeleteFirestoreImage
                                  }
                                  onClick={() =>
                                    (window.location.href = `/engine-correction/${item.id}`)
                                  }
                                />
                              )}
                              {isManualUpload && !isModerated && (
                                <>
                                  <SubMenuItem
                                    text={t('common:edit')}
                                    leftIconClassName="mt-0.5 ki-notepad-edit !text-[16.7px]"
                                    disabled={
                                      isModerated ||
                                      isPendingDeleteProcessing ||
                                      isPendingDeleteFirestoreImage
                                    }
                                    onClick={() => {
                                      setSelectedProcessing(item);
                                      showModal(
                                        '#engine-correction-upload-modal'
                                      );
                                    }}
                                  />
                                  <SubMenuItem
                                    text={t('common:delete')}
                                    leftIconClassName="ki-trash !text-[19px]"
                                    textClassName="line-clamp-1"
                                    disabled={
                                      isPendingDeleteProcessing ||
                                      isPendingDeleteFirestoreImage
                                    }
                                    onClick={() => {
                                      setSelectedProcessing(item);
                                      showModal(
                                        '#confirmation-remove-item-modal'
                                      );
                                    }}
                                  />
                                </>
                              )}
                            </TableMenu>
                          }
                        />
                      </tr>
                    </tbody>
                  );
                })
              )}
            </table>
          </div>
          <div className="flex flex-row justify-between">
            <div className="card-footer justify-center md:justify-between flex-col md:flex-row gap-3 text-gray-600 text-2sm font-medium">
              <div className="flex items-center gap-2">
                {t('common:show')}
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500 max-w-26"
                  options={[
                    {
                      label: '5',
                      value: '5',
                    },
                    {
                      label: '10',
                      value: '10',
                    },
                    {
                      label: '20',
                      value: '20',
                    },
                    {
                      label: '50',
                      value: '50',
                    },
                    {
                      label: '100',
                      value: '100',
                    },
                  ]}
                  value={filter.limit.toString()}
                  onChange={(e) =>
                    filterByLimit(Number.parseInt(e.target.value))
                  }
                />
                {t('common:perImage')}
              </div>
              <div className="flex items-center gap-4">
                <span data-datatable-info="true"></span>
                <div
                  className="pagination"
                  data-datatable-pagination="true"
                ></div>
              </div>
            </div>
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </Card>
      <EngineCorrectionUploadModal
        t={t}
        keyCarsData={keyCars?.data}
        defectKeyCarsData={defectKeyCars?.data}
        selectedProcessing={selectedProcessing}
        setSelectedProcessing={setSelectedProcessing}
        onUploadingImages={onUploadingImages}
      />
      <ImagePreviewModal
        id={'engine-correction-preview-modal'}
        bodyClassName="h-max max-h-[93.5vh] min-[1920px]:h-auto"
        onClose={() => {
          setSelectedProcessing(undefined);
        }}
        onClickOutsideContent={() => {
          setSelectedProcessing(undefined);
        }}
        onEscapePress={() => {
          setSelectedProcessing(undefined);
        }}
      >
        <img
          className="max-h-screen w-auto object-contain object-center"
          src={selectedProcessing?.imageUrl}
          alt=""
        />
      </ImagePreviewModal>
      <ConfirmationModal
        id="confirmation-remove-item-modal"
        title={t('common:confirmYourAction')}
        description={`${t('areYourSureToDeleteThisImage')} ?`}
        onConfirmLabel={t('common:delete')}
        onConfirm={() => {
          handleDeleteImage();
        }}
      />
      {isLoadingAll && <Loader />}
    </>
  );
};
